<script lang="ts">
import { defineComponent } from 'vue';
import type { SearchClient } from 'algoliasearch/lite';
import algoliasearch from 'algoliasearch/lite';

import {
  AisHits,
  AisInstantSearch,
  AisSearchBox,
  AisStateResults,
  AisStats,
} from 'vue-instantsearch/vue3/es';

export default defineComponent({
  components: {
    AisHits,
    AisInstantSearch,
    AisSearchBox,
    AisStateResults,
    AisStats,
  },
  props: {
    searchApiKey: {
      type: String,
      required: true,
    },
    appId: {
      type: String,
      required: true,
    },
    environment: {
      type: String,
      required: true,
    },
    isDesktop: {
      type: Boolean,
      required: true,
    },
    /**
     * Desktop is always enabled, mobile is enabled based on the trigger icon
     */
    isMobileEnabled: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['disableMobileSearch'],
  data() {
    return {
      algoliaClient: null as SearchClient | null,
      searchClient: null as SearchClient | null,
      future: { preserveSharedStateOnUnmount: true },
      queryHook(query: string, search: any) {
        // Only search if the query is at least 3 characters long
        if (query.length >= 3) {
          search(query);
        }
      },
    };
  },
  computed: {
    indexName() {
      return `${this.environment}_headerSearch`;
    },
  },
  watch: {
    /**
     * Open / close search if device width/orientation changes
     */
    isMobileEnabled(isMobileEnabled) {
      if (isMobileEnabled) {
        this.focusSearchInput();
      }
    },
  },
  created() {
    this.algoliaClient = algoliasearch(this.appId, this.searchApiKey);

    this.searchClient = {
      ...this.algoliaClient,
      search: (requests: any) => {
        // Skip search request if all requests have empty query parameters, still need to return a promise because Algolia expects one
        if (requests.every(({ params }: { params: any }) => !params.query)) {
          return Promise.resolve({
            results: requests.map(() => ({
              hits: [],
              nbHits: 0,
              page: 0,
              nbPages: 0,
              hitsPerPage: 20,
              processingTimeMS: 0,
              query: '',
              params: '',
            })),
          });
        }
        // Make the search request to Algolia
        return this.algoliaClient!.search(requests);
      },
    };
  },
  mounted() {
    // Add click outside listener to close search when clicking on the page
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    // Clean up the event listener when component is destroyed
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    focusSearchInput() {
      const { searchBox } = this.$refs as {
        searchBox: { $refs: { searchInput: { $refs: { input: HTMLInputElement } } } };
      };
      const { searchInput } = searchBox.$refs;
      const { input } = searchInput.$refs;
      // Focus the search input (using autofocus is only reliable on page load)
      input.focus();
    },
    handleClickOutside(event: MouseEvent) {
      const searchContainer = this.$el;
      // Check if the click was outside the search component
      if (searchContainer && !searchContainer.contains(event.target as Node)) {
        // Only clear if there's actually a search query
        const { searchBox } = this.$refs as {
          searchBox: any;
        };
        if (searchBox && searchBox.$refs && searchBox.$refs.searchInput) {
          const { searchInput } = searchBox.$refs;
          if (searchInput.$refs && searchInput.$refs.input) {
            const { input } = searchInput.$refs;
            // Only clear/close if there's text in the input
            if (input.value && input.value.length > 0) {
              this.clearSearch();
            }
          }
        }
      }
    },
    clearSearch() {
      const { instantSearch, searchBox } = this.$refs as {
        instantSearch: any;
        searchBox: any;
      };

      if (instantSearch && instantSearch.instantSearchInstance) {
        instantSearch.instantSearchInstance.setUiState({
          [this.indexName]: {
            query: '',
          },
        });
      }
      // Also clear the input field value directly and trigger events
      if (searchBox && searchBox.$refs && searchBox.$refs.searchInput) {
        const { searchInput } = searchBox.$refs;
        if (searchInput.$refs && searchInput.$refs.input) {
          const { input } = searchInput.$refs;
          input.value = '';
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      this.$emit('disableMobileSearch');
    },
    handleResetClick() {
      this.clearSearch();
    },
  },
});
</script>
<template>
  <Transition @after-enter="focusSearchInput">
    <div
      v-show="isMobileEnabled || isDesktop"
      id="header-search"
      class="relative order-last col-span-2 lg:-order-none lg:col-auto lg:ml-auto lg:h-13 lg:w-72 lg:overflow-visible"
    >
      <div class="lg:absolute lg:left-0 lg:right-0 lg:top-0 lg:z-50 lg:block">
        <div
          class="[&.v-enter-active]:transition-opacity [&.v-enter-from]:opacity-0 [&.v-leave-active]:transition-opacity [&.v-leave-to]:opacity-0"
        >
          <ais-instant-search
            ref="instantSearch"
            :search-client="searchClient"
            :index-name="indexName"
            :future="future"
          >
            <div
              class="border-gtg-claret focus-within:ring-gtg-claret rounded-full border-2 bg-gtg-pink-500 focus-within:ring-2 focus-within:ring-offset-4 focus-within:ring-offset-gtg-pink-700"
            >
              <ais-search-box
                ref="searchBox"
                show-loading-indicator
                placeholder="Search"
                :query-hook="queryHook"
                :class-names="{
                  'ais-SearchBox-form': 'relative',
                  'ais-SearchBox-input':
                    'bg-transparent py-2.5 border-0 focus:outline-none focus:ring-0 w-full px-6 pr-12 text-gtg-claret font-bold placeholder:text-gtg-claret [&::-webkit-search-cancel-button]:hidden',
                  'ais-SearchBox-reset':
                    '-translate-y-1/2 absolute right-11 top-1/2 cursor-pointer',
                  'ais-SearchBox-submit': '-translate-y-1/2 absolute right-4 top-1/2',
                  'ais-SearchBox-loadingIndicator': '-translate-y-1/2 absolute right-4 top-1/2',
                }"
                @keydown.esc="clearSearch"
              >
                <template #submit-icon>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    @click.prevent="focusSearchInput"
                  >
                    <path
                      d="M17 17L12.3333 12.3333M13.8889 8.44444C13.8889 11.4513 11.4513 13.8889 8.44444 13.8889C5.43756 13.8889 3 11.4513 3 8.44444C3 5.43756 5.43756 3 8.44444 3C11.4513 3 13.8889 5.43756 13.8889 8.44444Z"
                      stroke="#161818"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </template>
                <template #reset-icon>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    @click="handleResetClick"
                  >
                    <path
                      d="M15 5L5 15M5 5L15 15"
                      stroke="#161818"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </template>
              </ais-search-box>
            </div>
            <ais-stats>
              <template #default="{ nbHits, query }">
                <section
                  v-if="nbHits > 0 && query"
                  class="shadow-default mt-3 rounded-lg border border-gtg-grey-200 bg-white"
                >
                  <div class="px-4 pb-5 pt-3.5">
                    <div
                      class="max-h-[21rem] overflow-scroll pr-3 [scrollbar-color:theme(colors.gtg.pink.500)_theme(colors.gtg.grey.200)] [scrollbar-width:thin]"
                    >
                      <ais-state-results>
                        <ais-hits>
                          <template #default="{ items /* , sendEvent, query */ }">
                            <ul class="flex flex-col justify-start divide-y border-t">
                              <li v-for="item in items" :key="item.objectID">
                                <a :href="item.url" class="block py-2 text-base hover:underline">
                                  {{ item.title }}
                                </a>
                              </li>
                            </ul>
                          </template>
                        </ais-hits>
                      </ais-state-results>
                    </div>
                  </div>
                </section>
                <section
                  v-else-if="nbHits === 0 && query.length > 0"
                  class="shadow-default mt-3 rounded-lg border border-gtg-grey-200 bg-white"
                >
                  <div class="px-4 pb-5 pt-3.5">
                    <p class="border-t py-2 text-sm">No results have been found for {{ query }}.</p>
                  </div>
                </section>
                <!-- Blank for the else as we want no output -->
                <div v-else></div>
              </template>
            </ais-stats>
          </ais-instant-search>
        </div>
      </div>
    </div>
  </Transition>
</template>
